import React from 'react';
import { CarouselFrame, CarouselStage } from './ImageCarouselParts';
import type { PreviewAction } from './previewActionMenuModel';
import {
    useCarouselState,
    useContainerSize,
    useImagePreload,
    useThumbStrip,
    useValidImages,
    useZoomPan
} from './useImageCarousel';

const NO_ACTIONS: PreviewAction[] = [];

interface ImageCarouselProps {
    images: string[];
    className?: string;
    /** Optional per-image chip labels (e.g. `c-c-f 7/7`), indexed like `images`. */
    labels?: string[];
    /** Opt-in thumbnail pager, indexed like `images`; falls back to dots when
     *  the counts disagree. Pass small preview URLs, not full-size originals. */
    thumbnails?: string[];
    /** Mirrors the rail, toolbar and label — e.g. the left half of a compare
     *  pair, so both photos sit against the shared centre line. */
    pagerSide?: 'left' | 'right';
    /** Resets to the first image even when the image count is unchanged
     *  (e.g. switching between two scans with the same photo count). */
    resetKey?: string | number;
    /** Makes the shown image controlled; pair with `onIndexChange`. */
    index?: number;
    onIndexChange?: (index: number) => void;
    /** Listed under a "More actions" toolbar button; empty or absent = no button. */
    actions?: PreviewAction[];
}

export const ImageCarousel: React.FC<ImageCarouselProps> = ({
    images,
    className,
    labels,
    thumbnails,
    pagerSide = 'right',
    resetKey,
    index,
    onIndexChange,
    actions = NO_ACTIONS
}) => {
    const containerRef = React.useRef<HTMLDivElement | null>(null);
    const validImages = useValidImages(images);
    const thumbStrip = useThumbStrip(thumbnails, validImages.length);
    const view = useZoomPan(containerRef, useContainerSize(containerRef));
    const isImageLoading = useImagePreload(validImages);
    const state = useCarouselState({
        containerRef,
        imageCount: validImages.length,
        resetView: view.resetView,
        resetKey,
        index,
        onIndexChange
    });

    const mirrored = pagerSide === 'left';
    // Collapsing the strip falls back to dots so paging never disappears.
    const railThumbs = thumbStrip && state.showThumbnails ? thumbStrip : null;

    return (
        <CarouselFrame
            className={className}
            mirrored={mirrored}
            railThumbs={railThumbs}
            labels={labels}
            state={state}
        >
            <CarouselStage
                containerRef={containerRef}
                images={validImages}
                view={view}
                state={state}
                labels={labels}
                mirrored={mirrored}
                isLoading={isImageLoading(validImages[state.safeIndex])}
                showPager={!railThumbs}
                hasThumbStrip={!!thumbStrip}
                actions={actions}
            />
        </CarouselFrame>
    );
};

export default ImageCarousel;
